import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";
import OfflineBanner from "@/components/OfflineBanner";
import { PageSkeleton } from "@/components/Skeletons";

// Public / auth pages — kept eager for fast first paint
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import Consent from "@/pages/Consent";

// In-app pages (lazy)
const Teams = lazy(() => import("@/pages/Teams"));
const Players = lazy(() => import("@/pages/Players"));
const PlayerDetail = lazy(() => import("@/pages/PlayerDetail"));
const Games = lazy(() => import("@/pages/Games"));
const GameDetail = lazy(() => import("@/pages/GameDetail"));
const GameCompare = lazy(() => import("@/pages/GameCompare"));
const Practices = lazy(() => import("@/pages/Practices"));
const PracticeDetail = lazy(() => import("@/pages/PracticeDetail"));
const CoachingMode = lazy(() => import("@/pages/CoachingMode"));
const CoachAI = lazy(() => import("@/pages/CoachAI"));
const Settings = lazy(() => import("@/pages/Settings"));
const CoachDNA = lazy(() => import("@/pages/CoachDNA"));
const Journey = lazy(() => import("@/pages/Journey"));
const Reflect = lazy(() => import("@/pages/Reflect"));
const MonthlyReport = lazy(() => import("@/pages/MonthlyReport"));
const Legacy = lazy(() => import("@/pages/Legacy"));
const SeasonPlanner = lazy(() => import("@/pages/SeasonPlanner"));
const GameDay = lazy(() => import("@/pages/GameDay"));
const Goals = lazy(() => import("@/pages/Goals"));
const Admin = lazy(() => import("@/pages/Admin"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const BetaProgram = lazy(() => import("@/pages/BetaProgram"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const Billing = lazy(() => import("@/pages/Billing"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const PrivacyDashboard = lazy(() => import("@/pages/PrivacyDashboard"));

// Marketing pages (default exports)
const Pricing = lazy(() => import("@/pages/marketing/Pricing"));
const Features = lazy(() => import("@/pages/marketing/Features"));
const FAQ = lazy(() => import("@/pages/marketing/FAQ"));
const FoundingCoach = lazy(() => import("@/pages/marketing/FoundingCoach"));

// Named-export lazy wrappers
const namedLazy = (loader, name) => lazy(() => loader().then(m => ({ default: m[name] })));
const Privacy = namedLazy(() => import("@/pages/marketing/Legal"), "Privacy");
const Terms = namedLazy(() => import("@/pages/marketing/Legal"), "Terms");
const Cookies = namedLazy(() => import("@/pages/marketing/Legal"), "Cookies");
const Subprocessors = namedLazy(() => import("@/pages/marketing/Legal"), "Subprocessors");
const DataSafety = namedLazy(() => import("@/pages/marketing/Legal"), "DataSafety");
const About = namedLazy(() => import("@/pages/marketing/Misc"), "About");
const Contact = namedLazy(() => import("@/pages/marketing/Misc"), "Contact");
const ReleaseNotes = namedLazy(() => import("@/pages/marketing/Misc"), "ReleaseNotes");
const Blog = namedLazy(() => import("@/pages/marketing/Misc"), "Blog");
const Resources = namedLazy(() => import("@/pages/marketing/Misc"), "Resources");
const HelpPublic = namedLazy(() => import("@/pages/marketing/Misc"), "HelpPublic");
const BetaInfo = namedLazy(() => import("@/pages/marketing/Misc"), "BetaInfo");

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="h-screen w-screen flex items-center justify-center text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Gate the app until Google-signed-in users complete consent.
  if (user.needs_consent && location.pathname !== "/consent") {
    return <Navigate to="/consent" replace />;
  }
  return <AppShell>{children}</AppShell>;
}

/** Detect the #session_id= fragment synchronously during render so we always
 *  process Emergent OAuth callbacks BEFORE any other route (including protected ones). */
function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>}>
      <Routes>
              {/* Public / marketing */}
              <Route path="/" element={<Landing />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/founding-coach" element={<FoundingCoach />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/legal/subprocessors" element={<Subprocessors />} />
              <Route path="/legal/data-safety" element={<DataSafety />} />
              <Route path="/release-notes" element={<ReleaseNotes />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/help-public" element={<HelpPublic />} />
              <Route path="/beta-info" element={<BetaInfo />} />

              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/consent" element={<Consent />} />

              {/* App */}
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/season" element={<Protected><SeasonPlanner /></Protected>} />
              <Route path="/game-day" element={<Protected><GameDay /></Protected>} />
              <Route path="/coach-dna" element={<Protected><CoachDNA /></Protected>} />
              <Route path="/journey" element={<Protected><Journey /></Protected>} />
              <Route path="/reflect" element={<Protected><Reflect /></Protected>} />
              <Route path="/monthly-report" element={<Protected><MonthlyReport /></Protected>} />
              <Route path="/legacy" element={<Protected><Legacy /></Protected>} />
              <Route path="/goals" element={<Protected><Goals /></Protected>} />
              <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
              <Route path="/beta" element={<Protected><BetaProgram /></Protected>} />
              <Route path="/help" element={<Protected><HelpCenter /></Protected>} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/teams" element={<Protected><Teams /></Protected>} />
              <Route path="/players" element={<Protected><Players /></Protected>} />
              <Route path="/players/:id" element={<Protected><PlayerDetail /></Protected>} />
              <Route path="/games" element={<Protected><Games /></Protected>} />
              <Route path="/games/compare" element={<Protected><GameCompare /></Protected>} />
              <Route path="/games/:id" element={<Protected><GameDetail /></Protected>} />
              <Route path="/practices" element={<Protected><Practices /></Protected>} />
              <Route path="/practices/:id" element={<Protected><PracticeDetail /></Protected>} />
              <Route path="/practices/:id/run" element={<Protected><CoachingMode /></Protected>} />
              <Route path="/coach-ai" element={<Protected><CoachAI /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="/settings/billing" element={<Protected><Billing /></Protected>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <OfflineBanner />
          <Toaster position="top-center" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
